import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import AddItemToStoreModal from './AddItemToStoreModal';
import ClassificationTree from './ClassificationTree';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import SmartPrintButton from './PrintButton';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import {
  Save, Search, Tag, Package, Box, Settings, GitBranch, List,
  Trash2, RefreshCw, ArrowLeft, Info, CreditCard, ShieldCheck, Database, Plus, X, Filter, ChevronRight, Paperclip, ChevronLeft,
  History as HistoryIcon
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import CustomFieldSlot from './CustomFieldSlot';

const ItemDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';

  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [activeTab, setActiveTab] = useState('ITEM');
  const [fieldMaps, setFieldMaps] = useState({});
  const [showClassTree, setShowClassTree] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSpecIdx, setSelectedSpecIdx] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const { canCreate, canUpdate, canDelete } = usePermissions('items');
  const [count, setCount] = useState(0);
  const [nav, setNav] = useState({ total: 0, current_pos: 0, next: null, prev: null, first: null, last: null });
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [data, setData] = useState({
    itemnum: '', description: '', itemsetid: 'ITEMSET1', itemtype: '', status: 'ACTIVE',
    orderunit: 'EACH', issueunit: 'EACH', lotcontrol: false, rotating: false,
    inspection_required: false, condition: false, sparepart: false, classstructureid: null,
    inventory: [], specifications: [], assembly_structure: [], vendors:[]
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', filters: {} });

  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`/api/items/${id}/navigation${location.search}`);
      setNav(res.data);
    } catch (err) { console.error("Navigation load failed"); }
  }, [id, isNew, location.search]);

  const goTo = (targetId) => { if (targetId) navigate(`/items/${targetId}${location.search}`); };

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const fetchCount = useCallback(async () => {
    if (!data.itemnum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/ITEM/${data.itemnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error("Error fetching attachment count:", err); }
  }, [data.itemnum, isNew]);

  useEffect(() => {
    if (isNew || !data.itemnum) {
        setData(prev => ({
          ...prev,
          description: '', itemsetid: 'ITEMSET1', itemtype: '', status: 'ACTIVE',
          orderunit: 'EACH', issueunit: 'EACH', lotcontrol: false, rotating: false,
          inspection_required: false, classstructureid: null,
          inventory: [], specifications: [], assembly_structure: []
        }));
        axios.get(`/api/item/next-number/'DEFAULT'}`)
            .then(res => { setData(prev => ({ ...prev, itemnum: res.data.next_itmnum })); });
    }
  }, [isNew, data.itemnum]);

  const loadData = async () => {
    try {
      const [assetMaps, meterMaps] = await Promise.all([
        axios.get('/api/admin/field-map/items'),
        axios.get('/api/admin/field-map/Meters')
      ]);

      setFieldMaps({ ...(assetMaps.data || {}), ...(meterMaps.data || {}) });

      if (id && id !== 'new') {
        const res = await axios.get(`/api/items/${id}`);
        const sanitizedData = {
            ...res.data,
            inventory: res.data.inventory || [],
            specifications: res.data.specifications || [],
            assembly_structure: res.data.assembly_structure || [],
            vendors: res.data.vendors || []
        };
        setData(sanitizedData);
        loadNavigation();
      }
    } catch (err) {
      console.error("Error loading item data or mappings:", err);
      setFieldMaps({});
    }
  };

  useEffect(() => { loadData(); }, [id, loadNavigation]);
  useEffect(() => { fetchCount(); }, [data.itemnum, fetchCount]);

  const handleClassSelect = async (node) => {
    try {
      const loadId = toast.loading(t('itemDetails.messages.fetchingAttributes'));
      const res = await axios.get(`/api/classifications/${node.classstructureid}/attributes`);

      const newSpecs = res.data.map(attr => ({
        assetattrid: attr.assetattrid, description: attr.description, datatype: attr.datatype,
        uom: attr.uom, domainid: attr.domainid, alnvalue: '', numvalue: null
      }));

      setData(prev => ({
        ...prev, classstructureid: node.classstructureid,
        classstructureid_description: node.description, specifications: newSpecs
      }));

      toast.success(t('itemDetails.messages.attributesLoaded'), { id: loadId });
      setShowClassTree(false);
    } catch (err) { toast.error(t('itemDetails.messages.failedAttributes')); }
  };

  const handleSmartSearch = (fieldName, title, defaultType) => {
    const linkedDomain = fieldMaps[fieldName];
    let extraFilters = {};
    if (fieldName === 'siteid' && data.orgid) { extraFilters = { org_id: data.orgid }; }

    setLookup({
        isOpen: true, title: title, type: linkedDomain ? 'DOMAIN' : defaultType,
        domainId: linkedDomain || '', field: fieldName, filters: extraFilters
    });
  };

  const handleInputChange = (field, value) => { setData(prev => ({ ...prev, [field]: value })); };

  const handleSpecUpdate = (field, value) => {
    if (selectedSpecIdx === null) return;
    const newList = [...data.specifications];
    newList[selectedSpecIdx][field] = value;
    setData({ ...data, specifications: newList });
  };

  const handleSave = async () => {
    if (!data.itemnum || !data.description) return toast.error(t('itemDetails.messages.reqFields'));
    const loadId = toast.loading(t('common.saving'));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/items/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(t('common.save_success'), { id: loadId });
      if (isNew) navigate(`/items/${data.itemnum}`);
    } catch (err) { toast.error(t('common.save_fail'), { id: loadId }); }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: t('common.are_you_sure'), text: t('itemDetails.swal.deleteText', { num: data.itemnum }),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.delete'), cancelButtonText: t('common.cancel'),
      customClass: { popup: 'font-sans text-[12px] uppercase' }
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/items/${data.itemnum}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success(t('common.delete_success'));
        navigate('/items');
      } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail')); }
    }
  };

  useEffect(() => {
    const openStatusModal = () => { setShowStatusModal(true); };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, []);

  useEffect(() => {
    const AddModalOpen = () => { setIsAddModalOpen(true); };
    window.addEventListener('OPEN_ADD_ITEM_STOREROOM', AddModalOpen);
    return () => window.removeEventListener('OPEN_ADD_ITEM_STOREROOM', AddModalOpen);
  }, []);

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  useEffect(() => {
    const handleOpenHistory = () => { setShowHistoryModal(true); };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => { window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory); };
  }, []);

  const handleAddAssemblyItem = () => {
      setLookup({
          isOpen: true, title: t('itemDetails.lookups.selectChild'), type: 'items', field: 'child_itemnum',
          onCustomSelect: (row) => {
              const newItem = { child_itemnum: row.itemnum, quantity: 1, remarks: row.description };
              setData(prev => ({ ...prev, assembly_structure: [...(prev.assembly_structure || []), newItem] }));
          }
      });
  };

  const addNewSpecification = () => {
    const newSpec = { assetattrid: '', description: '', datatype: 'ALN', alnvalue: '', numvalue: null, uom: '', domainid: null };
    setData({ ...data, specifications: [...(data.specifications || []), newSpec] });
    setSelectedSpecIdx((data.specifications || []).length);
  };

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[11px] overflow-hidden uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }}>

      {/* --- Modals --- */}
      <UniversalLookup
        {...lookup} onClose={() => setLookup({...lookup, isOpen: false})}
        onSelect={(row) => {
          if (lookup.onCustomSelect) { lookup.onCustomSelect(row); }
          else {
            const val = row.assetnum || row.location_id || row.itemnum || row.meter_num || row.value || row.id || row.site_id;
            handleInputChange(lookup.field, val);
          }
          setLookup(prev => ({ ...prev, isOpen: false }));
        }}
      />

      <ClassificationTree isOpen={showClassTree} onClose={() => setShowClassTree(false)} onSelect={handleClassSelect} />

      <AddItemToStoreModal
        isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} itemnum={data.itemnum}
        onRefresh={() => { axios.get(`/api/items/${data.itemnum}`).then(res => setData(res.data)); }}
      />
      {showStatusModal && (
        <StatusModal
          currentStatus={data.status} recordId={data.itemnum} appName="ITEMS"
          domainId={fieldMaps['status']} onClose={() => setShowStatusModal(false)} onComplete={loadData}
          onComplete={() => window.location.reload()}
        />
      )}
      {showHistoryModal && <StatusHistoryModal appName="ITEMS" recordId={data.itemnum} onClose={() => setShowHistoryModal(false)} />}
      <AttachmentsModal isOpen={isDocModalOpen} onClose={() => { setIsDocModalOpen(false); fetchCount(); }} ownertable="ITEM" ownerid={data.itemnum} />
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="items" recordId={data.itemnum} />

      {/* --- 1. TOP DARK TOOLBAR --- */}
      <div className={`bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 `}>
        <div className={`flex items-center gap-3 `}>
          <button onClick={() => navigate('/items')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/70">
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>

          {!isNew && (
            <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 `}>
              <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-status-warning font-black">{nav.current_pos}</span> / {nav.total}</div>
              <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
              <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 `}>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
            <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] transition-all shadow-md uppercase font-black active:scale-95">
              <Save size={14} /> {t('itemDetails.actions.save')}
            </button>
          )}
          <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
          {!isNew && <SmartPrintButton moduleName="ITEM" refId={data.itemnum} />}
          {(!isNew && canUpdate) && (
            <button onClick={() => setShowStatusModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('common.change_status')}><Tag size={16}/></button>
          )}
          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70">
            <Paperclip size={16} />
            {count > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black animate-pulse">{count}</span>}
          </button>
          <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('common.history')}><HistoryIcon size={16}/></button>
          {!isNew && canDelete && (
            <button onClick={handleDelete} className="p-2 hover:bg-status-danger/20 rounded text-status-danger transition-colors" title={t('common.delete')}><Trash2 size={16}/></button>
          )}
        </div>
      </div>

      {/* --- 2. SUMMARY HEADER --- */}
      <div className={`bg-surface-card border-b border-line px-6 py-3 flex items-center gap-12 shadow-sm z-20 `}>
        <div className={`flex flex-col min-w-[120px] ${isRTL ? 'border-l pl-8' : 'border-r pr-8'}`}>
          <label className="text-[0.9em] text-ink-secondary font-black uppercase tracking-widest">{t('itemList.tables.item')}</label>
          <span className="text-sm font-black text-primary-dark">{data.itemnum || 'NEW_ITEM'}</span>
        </div>
        <div className={`flex flex-col flex-1 ${isRTL ? 'border-l pl-8' : 'border-r pr-8'}`}>
          <label className="text-[0.9em] text-ink-secondary font-black uppercase tracking-widest">{t('assets.headers.desc')}</label>
          <input className={`text-sm font-bold text-ink outline-none focus:text-primary bg-transparent w-full transition-all ${isRTL ? 'text-right' : 'text-left'}`} value={data.description} onChange={e => handleInputChange('description', e.target.value)} />
        </div>
        <div className="flex flex-col min-w-[100px]">
          <label className="text-[0.9em] text-ink-secondary font-black uppercase tracking-widest">{t('assets.headers.status')}</label>
          <span className={`text-[10px] font-black px-2 py-0.5 rounded-full w-fit mt-1 ${data.status === 'ACTIVE' ? 'bg-status-success-bg text-status-success' : 'bg-status-danger-bg text-status-danger'}`}> {data.status} </span>
        </div>
      </div>

      {/* --- 3. Tabs --- */}
      <div className={`flex bg-primary-dark text-white overflow-x-auto no-scrollbar shadow-md `}>
        {[
          { id: 'ITEM', icon: <Tag size={14}/>, label: t('itemDetails.tabs.item') },
          { id: 'INVENTORY', icon: <Package size={14}/>, label: t('itemDetails.tabs.inventory') },
          { id: 'VENDORS', icon: <Box size={14}/>, label: t('itemDetails.tabs.vendors') },
          { id: 'SPECS', icon: <Settings size={14}/>, label: t('itemDetails.tabs.specs') },
          { id: 'ASSEMBLY', icon: <GitBranch size={14}/>, label: t('itemDetails.tabs.assembly') }
        ].map(tObj => (
          <button key={tObj.id} onClick={() => setActiveTab(tObj.id)} className={`px-8 py-3 flex items-center gap-2 font-bold transition-all whitespace-nowrap ${isRTL ? 'border-l border-white/5 ' : 'border-r border-white/5'} ${activeTab === tObj.id ? 'bg-surface-card text-primary-dark shadow-inner' : 'hover:bg-white/10 opacity-75'}`}>
            {tObj.icon} {tObj.label}
          </button>
        ))}
      </div>

      {/* --- 4. Content Area --- */}
      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        {activeTab === 'ITEM' ? (
            <div className="bg-surface-card p-10 border shadow-sm max-w-5xl mx-auto space-y-10 animate-in fade-in duration-500 rounded-sm border-t-4 border-t-primary">
                <div className={`grid grid-cols-2 gap-24 ${isRTL ? 'dir-rtl' : ''}`}>
                    <div className="space-y-6">
                        <h3 className={`text-[10px] font-black text-ink-secondary border-b pb-2 uppercase tracking-[0.2em] flex items-center gap-2 `}><Info size={14} className="text-primary"/> {t('itemDetails.sections.config')}</h3>
                        <EditableField label={t('assets.headers.desc')} value={data.description} onChange={v => handleInputChange('description', v)} isRequired isRTL={isRTL}/>
                        <StaticField label={t('itemDetails.fields.itemSet')} value={data.itemsetid} isRTL={isRTL} />
                        <LookupField label={t('type')} value={data.itemtype} onSearch={() => handleSmartSearch('itemtype', t('common.search'), 'DOMAIN')} isRTL={isRTL} />
                        <StaticField label={t('assets.headers.status')} value={data.status} isRTL={isRTL} />
                        <div className="flex flex-col gap-4 pt-4 border-t border-line-subtle font-black text-ink-secondary">
                            <CheckboxField label={t('itemDetails.fields.rotating')} checked={data.rotating} onChange={v => handleInputChange('rotating', v)} isRTL={isRTL} />
                            <CheckboxField label={t('itemDetails.fields.lotControl')} checked={data.lotcontrol} onChange={v => handleInputChange('lotcontrol', v)} isRTL={isRTL} />
                            <CheckboxField label={t('itemDetails.fields.inspectionReq')} checked={data.inspection_required} onChange={v => handleInputChange('inspection_required', v)} isRTL={isRTL} />
                        </div>
                        <CustomFieldSlot coreModelName="ITEM" tab="ITEM" section="Configuration" recordId={data.itemnum} disabled={isNew} />
                    </div>
                    <div className="space-y-6">
                        <h3 className={`text-[10px] font-black text-ink-secondary border-b pb-2 uppercase tracking-[0.2em] flex items-center gap-2 `}><CreditCard size={14} className="text-status-success"/> {t('itemDetails.sections.units')}</h3>
                        <DataField label={t('itemDetails.fields.orderUnit')} value={data.orderunit} onSearch={() => handleSmartSearch('orderunit', t('common.search'), 'DOMAIN')} isRTL={isRTL} />
                        <LookupField label={t('itemDetails.fields.issueUnit')} value={data.issueunit} onSearch={() => handleSmartSearch('issueunit', t('common.search'), 'DOMAIN')} isRTL={isRTL} />
                        <CheckboxField label={t('itemDetails.fields.condition')} checked={data.condition} onChange={v => handleInputChange('condition', v)} isRTL={isRTL} />
                        <CheckboxField label={t('itemDetails.fields.sparepart')} checked={data.sparepart} onChange={v => handleInputChange('sparepart', v)} isRTL={isRTL} />
                        <CustomFieldSlot coreModelName="ITEM" tab="ITEM" section="Units" recordId={data.itemnum} disabled={isNew} />
                        <div className={`bg-primary-soft/50 p-6 rounded border-primary-dark mt-16 shadow-inner ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
                            <div className={`flex items-center gap-2 text-primary-dark font-black mb-1 uppercase tracking-widest text-[9px] `}><ShieldCheck size={16}/> <span>{t('itemDetails.messages.stockMsgTitle')}</span></div>
                            <p className="text-ink-secondary italic text-[10px] leading-relaxed uppercase">{t('itemDetails.messages.stockMsgDesc')}</p>
                        </div>
                    </div>
                </div>
            </div>
        ) : activeTab === 'INVENTORY' ? (
            <div className="bg-surface-card border shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-500 border-t-4 border-t-primary-dark">
                <InventoryTable inventory={data.inventory} onAdd={() => setIsAddModalOpen(true)} isRTL={isRTL} t={t} />
            </div>
        ) : activeTab === 'SPECS' ? (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom-4 duration-500">
            <div className={`bg-surface-card border p-4 shadow-sm rounded-sm flex flex-col md:flex-row items-end gap-4 ${isRTL ? '' : 'justify-between'}`}>
              <div className="flex-1 w-full"><EditableField label={t('itemDetails.fields.classification')} value={data.classstructureid_description || data.classstructureid} onSearch={() => setShowClassTree(true)} isRTL={isRTL}/></div>
              <button onClick={addNewSpecification} className={`bg-primary text-white px-6 py-1.5 rounded-sm font-bold shadow hover:bg-primary-dark transition-all text-xs flex items-center gap-2 active:scale-95 `}><Plus size={14} /> {t('itemDetails.actions.newSpec')}</button>
            </div>
            <div className="bg-surface-card border rounded shadow-sm overflow-hidden min-h-[400px]">
               <SpecsTable specs={data.specifications} selectedIdx={selectedSpecIdx} onSelect={setSelectedSpecIdx} isRTL={isRTL} t={t} />
               {selectedSpecIdx !== null && data.specifications?.[selectedSpecIdx] && (
                  <div className={`p-6 border-t bg-surface-panel grid grid-cols-2 gap-10 ${isRTL ? 'dir-rtl' : ''}`}>
                      <EditableField label={t('itemDetails.tables.attribute')} value={data.specifications[selectedSpecIdx].assetattrid} onSearch={() => handleSmartSearch('assetattrid', t('common.search'), 'attributes')} isRTL={isRTL} />
                      <div className={`flex flex-col gap-1 border-b border-line ${isRTL ? 'text-right' : 'text-left'}`}>
                          <label className="text-ink-secondary font-bold text-[9px] uppercase">{t('itemDetails.tables.value')}</label>
                          <input className={`outline-none py-1 bg-transparent font-bold text-primary ${isRTL ? 'text-right' : 'text-left'}`} value={data.specifications[selectedSpecIdx].alnvalue || data.specifications[selectedSpecIdx].numvalue || ''} onChange={e => handleSpecUpdate('alnvalue', e.target.value)} />
                      </div>
                  </div>
               )}
            </div>
          </div>
        ) : activeTab === 'ASSEMBLY' ? (
            <div className="bg-surface-card border shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-500 border-t-4 border-t-purple-900">
                <AssemblyTable assembly={data.assembly_structure} onAdd={handleAddAssemblyItem} onDelete={(idx) => {const nl = [...data.assembly_structure]; nl.splice(idx,1); setData({...data, assembly_structure:nl});}} onUpdate={(idx,f,v) => {const nl = [...data.assembly_structure]; nl[idx][f]=v; setData({...data, assembly_structure:nl});}} isRTL={isRTL} t={t} />
            </div>
        ) : activeTab === 'VENDORS' ? (
            <div className="bg-surface-card border shadow-2xl max-w-6xl mx-auto rounded-sm overflow-hidden animate-in slide-in-from-right duration-500">
                <VendorsTable vendors={data.vendors} isRTL={isRTL} t={t} />
            </div>
        ) : (
            <div className="bg-surface-card p-20 text-center text-ink-secondary font-bold uppercase tracking-[0.4em] border shadow-sm max-w-5xl mx-auto">{t('common.no_data')}</div>
        )}
      </div>
    </div>
  );
};

// --- Sub Components ---
const CheckboxField = ({ label, checked, onChange, isRTL }) => (
  <label className={`flex items-center gap-3 cursor-pointer group ${isRTL ? ' text-right' : ''}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="w-4 h-4 accent-primary rounded-sm" />
      <span className="group-hover:text-primary transition-colors uppercase">{label}</span>
  </label>
);

const InventoryTable = ({ inventory = [], onAdd, isRTL, t }) => (
    <div className="flex flex-col">
        <div className={`bg-surface-panel p-3 border-b flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
          <button onClick={onAdd} className={`bg-primary text-white px-6 py-1.5 rounded-sm font-bold text-[1.0em] flex items-center gap-2 shadow-md hover:bg-primary-dark transition-all active:scale-95 uppercase `}><Plus size={14}/> {t('itemDetails.actions.addStore')}</button>
        </div>
        <table className={`w-full border-collapse font-bold uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-surface-panel border-b text-ink-secondary text-[0.9em] tracking-widest">
              <tr className={isRTL ? '' : ''}>
                <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.store')}</th>
                <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.site')}</th>
                <th className={`p-4 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.avgCost')}</th>
                <th className={`p-4 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.balance')}</th>
                <th className="p-4 text-center">{t('assets.headers.status')}</th>
              </tr>
            </thead>
            <tbody className="text-[11px] divide-y divide-line-subtle">
                {inventory.map((item, idx) => (
                    <tr key={idx} className={`hover:bg-primary-soft/50 `}>
                      <td className={`p-4 border-x text-primary ${isRTL ? 'border-l' : 'border-r'}`}>{item.location}</td>
                      <td className={`p-4 border-x text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{item.siteid}</td>
                      <td className={`p-4 border-x text-center font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{parseFloat(item.avgcost || 0).toFixed(2)}</td>
                      <td className={`p-4 border-x text-center text-status-success font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{parseFloat(item.curbal || 0).toFixed(2)}</td>
                      <td className="p-4 text-center font-black">ACTIVE</td>
                    </tr>
                ))}
                {inventory.length === 0 && <tr><td colSpan="5" className="p-16 text-center text-ink-secondary italic tracking-widest">{t('itemDetails.messages.noInventory')}</td></tr>}
            </tbody>
        </table>
    </div>
);

const SpecsTable = ({ specs = [], selectedIdx, onSelect, isRTL, t }) => (
    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
      <thead className="bg-surface-panel border-b text-ink-secondary font-bold text-[0.9em] uppercase tracking-widest">
        <tr className={isRTL ? '' : ''}>
          <th className="p-3 w-10 border-x"></th>
          <th className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.attribute')}</th>
          <th className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc')}</th>
          <th className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.value')}</th>
          <th className="p-3 border-x">{t('itemDetails.tables.uom')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line-subtle">
        {specs.map((spec, i) => (
          <tr key={i} onClick={() => onSelect(i)} className={`hover:bg-primary-soft cursor-pointer text-[11px] ${selectedIdx === i ? 'bg-primary-soft' : ''} `}>
            <td className="p-3 text-center text-primary border-x">{selectedIdx === i && <ChevronRight size={14} className={isRTL ? 'rotate-180 mx-auto' : 'mx-auto'}/>}</td>
            <td className={`p-3 border-x font-bold text-primary ${isRTL ? 'border-l' : 'border-r'}`}>{spec.assetattrid || '---'}</td>
            <td className={`p-3 border-x text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}>{spec.description || '---'}</td>
            <td className={`p-3 border-x font-bold text-status-success ${isRTL ? 'border-l' : 'border-r'}`}>{spec.alnvalue || spec.numvalue || '---'}</td>
            <td className="p-3 text-ink-secondary border-x">{spec.uom || '---'}</td>
          </tr>
        ))}
        {specs.length === 0 && <tr><td colSpan="5" className="p-16 text-center text-ink-secondary italic tracking-widest uppercase font-black">{t('itemDetails.messages.noSpecs')}</td></tr>}
      </tbody>
    </table>
);

const AssemblyTable = ({ assembly = [], onAdd, onDelete, onUpdate, isRTL, t }) => (
    <div className="flex flex-col">
      <div className={`bg-surface-panel p-3 border-b flex px-8 items-center ${isRTL ? 'justify-between ' : 'justify-between'}`}>
        <span className="text-[1.0em] font-black italic tracking-widest text-ink-secondary">{t('itemDetails.sections.bomList')}</span>
        <button onClick={onAdd} className={`bg-purple-600 text-white px-6 py-1.5 rounded-sm font-bold text-[10px] shadow-md hover:bg-purple-800 transition-all uppercase active:scale-95 flex items-center gap-2 `}><Plus size={14}/> {t('itemDetails.actions.addComponent')}</button>
      </div>
      <table className={`w-full border-collapse font-bold uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
        <thead className="bg-surface-panel border-b text-ink-secondary text-[0.9em] tracking-widest">
          <tr className={isRTL ? '' : ''}>
            <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.subItem')}</th>
            <th className={`p-4 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.qty')}</th>
            <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.remarks')}</th>
            <th className="p-4 w-12 text-center text-status-danger border-x"><Trash2 size={12} className="mx-auto"/></th>
          </tr>
        </thead>
        <tbody className="text-[11px] divide-y divide-line-subtle">
          {assembly.map((row, i) => (
            <tr key={i} className={`hover:bg-primary-soft `}>
              <td className={`p-4 border-x text-primary ${isRTL ? 'border-l' : 'border-r'}`}>{row.child_itemnum}</td>
              <td className={`p-4 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}><input type="number" className="w-20 bg-transparent text-center outline-none font-mono" value={row.quantity} onChange={e => onUpdate(i, 'quantity', e.target.value)} /></td>
              <td className={`p-4 border-x italic text-ink-secondary ${isRTL ? 'border-l' : 'border-r'}`}><input className={`w-full bg-transparent outline-none ${isRTL ? 'text-right' : 'text-left'}`} value={row.remarks || ''} onChange={e => onUpdate(i, 'remarks', e.target.value)} /></td>
              <td className="p-4 border-x text-center"><button onClick={() => onDelete(i)} className="text-ink-secondary hover:text-status-danger transition-colors"><X size={14} className="mx-auto"/></button></td>
            </tr>
          ))}
          {assembly.length === 0 && <tr><td colSpan="4" className="p-16 text-center text-ink-secondary italic tracking-widest uppercase font-black">{t('itemDetails.messages.noBom')}</td></tr>}
        </tbody>
      </table>
    </div>
);

const VendorsTable = ({ vendors = [], isRTL, t }) => (
    <div className="flex flex-col animate-in fade-in duration-500">
        <div className={`bg-surface-panel p-3 border-b border-t-4 border-t-status-success flex justify-between items-center px-8 shadow-sm `}>
            <span className={`text-[1.0em] font-black text-status-success tracking-widest italic flex items-center gap-2 `}>
                <Box size={14} className="text-status-success"/> {t('itemDetails.sections.supplierHistory')}
            </span>
            <span className="bg-status-success-bg text-status-success px-3 py-1 rounded-full text-[0.8em] font-black border border-status-success">
                {t('itemDetails.tables.activeVendors')}: {vendors.length}
            </span>
        </div>
        <table className={`w-full border-collapse font-bold uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-surface-panel border-b text-ink-secondary text-[0.9em] tracking-widest">
                <tr>
                    <th className={`p-4 border-x w-48 ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.vendor')}</th>
                    <th className={`p-4 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.supplierName')}</th>
                    <th className={`p-4 border-x text-center w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('itemDetails.tables.unitCost')}</th>
                    <th className={`p-4 border-x text-center w-24 ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.qty')}</th>
                    <th className="p-4 border-x w-32">{t('itemDetails.tables.refPO')}</th>
                </tr>
            </thead>
            <tbody className="text-[1.0em] divide-y divide-line-subtle">
                {vendors.length > 0 ? vendors.map((v, i) => {
                    const cost = v.unitcost && !isNaN(v.unitcost) ? parseFloat(v.unitcost) : 0;
                    return (
                        <tr key={i} className={`hover:bg-status-success-bg/50 transition-colors group `}>
                            <td className={`p-4 border-x text-primary font-black ${isRTL ? 'border-l' : 'border-r'}`}>{v.vendor_code || v.vendor || '---'}</td>
                            <td className={`p-4 border-x text-ink-secondary italic ${isRTL ? 'border-l' : 'border-r'}`}>{v.vendor_name || 'REGISTERED SUPPLIER'}</td>
                            <td className={`p-4 border-x text-center font-mono text-status-success ${isRTL ? 'border-l' : 'border-r'}`}>
                                {cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                <span className={`text-[8px] text-ink-secondary font-sans ${isRTL ? 'mr-1' : 'ml-1'}`}>{v.currency || 'EGP'}</span>
                            </td>
                            <td className={`p-4 border-x text-center font-mono text-ink ${isRTL ? 'border-l' : 'border-r'}`}>{v.quantity || 0}</td>
                            <td className="p-4 border-x text-ink-secondary font-medium">{v.ponum || 'STOCK ADJ'}</td>
                        </tr>
                    );
                }) : (
                    <tr><td colSpan="5" className="p-20 text-center text-ink-secondary font-black italic tracking-widest uppercase">{t('itemDetails.messages.noVendors')}</td></tr>
                )}
            </tbody>
        </table>
    </div>
);

const StaticField = ({ label, value, isRTL }) => (
  <div className={`flex flex-col gap-1 border-b border-line-subtle pb-1 uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-ink-secondary font-black text-[0.8em] tracking-widest">{label}</label>
    <div className="font-bold text-ink min-h-[15px] px-1">{value || ''}</div>
  </div>
);

const DataField = ({ label, value, onSearch, isRTL }) => (
  <div className={`flex flex-col border-b border-line-subtle py-1.5 group hover:bg-primary-soft/30 transition-all rounded px-1 ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.9em] font-bold text-ink-secondary uppercase">{label}</label>
    <div className={`flex justify-between items-center h-5 `}>
      <span className="font-bold uppercase text-primary">{value || ''}</span>
      {onSearch && <Search size={13} className="text-primary cursor-pointer hover:text-primary-dark" onClick={onSearch} />}
    </div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, isRequired, placeholder, isRTL }) => (
  <div className={`flex flex-col gap-1 border-b border-line focus-within:border-primary transition-all uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className={`${isRequired ? 'text-status-danger' : 'text-ink-secondary'} font-black text-[0.9em]`}>{isRequired && '* '} {label}</label>
    <div className={`flex justify-between items-center `}>
      <input className={`font-black text-primary outline-none py-1.5 bg-transparent text-[11px] px-1 flex-1 ${isRTL ? 'text-right' : 'text-left'}`} value={value || ''} placeholder={placeholder} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={14} className="text-primary cursor-pointer hover:scale-110" onClick={onSearch} />}
    </div>
  </div>
);

const LookupField = ({ label, value, onSearch, isRTL }) => (
  <div className={`flex flex-col border-b border-line group focus-within:border-primary transition-all uppercase ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[0.9em] font-black text-ink-secondary uppercase">{label}</label>
    <div className={`flex items-center justify-between py-1 px-1 `}>
      <input className={`font-black text-primary outline-none bg-transparent flex-1 text-[11px] ${isRTL ? 'text-right' : 'text-left'}`} value={value || ''} readOnly />
      <div className="p-1 hover:bg-primary-soft rounded-full cursor-pointer" onClick={onSearch}><Search size={14} className="text-primary" /></div>
    </div>
  </div>
);

export default ItemDetails;
